// /search — find entities with the query language and narrow them down.
//
// The query text is the only state (?q=, plus ?sort= and ?page=), so every
// result list is a shareable link. Facets in the sidebar edit the query
// (type:actor, country:RU, verdict:real) instead of hidden filter state, so
// what you see in the box is exactly what is searched. Explore (/explore)
// stays the browse/dashboard view and sends searches here.
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import QueryInput, { SEARCH_EXAMPLES } from '../../components/QueryInput';
import SearchResults from '../explore/SearchResults';
import {
  fetchExploreCategories, searchEntities,
  type ExploreCategoriesResponse, type ExploreSearchResponse, type ExploreSearchResult,
  type FacetBucket, type QueryClause,
} from '../../lib/exploreApi';
import { entityDetailPath } from '../../lib/paths';
import { categoryColor } from '../../lib/entityColors';
import { countryFlag, countryLabel } from '../../lib/countryFlag';
import '../explore/exploreSearch.css';
import './SearchPage.css';

const DEBOUNCE_MS = 300;
const PAGE_SIZE = 50;
const FACET_PREVIEW = 8;

const INTEL_LABELS: Record<string, string> = {
  actor: 'Actors', family: 'Families', technique: 'Techniques', cve: 'CVEs', country: 'Countries',
};

/** Remove query[start, end) and tidy the whitespace left behind. */
function cutSpan(q: string, start: number, end: number): string {
  return (q.slice(0, start) + ' ' + q.slice(end)).replace(/\s+/g, ' ').trim();
}

/** Facets are single-choice per field: clicking the active value removes
 *  it, clicking another replaces it (two types AND-ed would match nothing). */
function toggleFacet(q: string, field: string, value: string): string {
  const term = `${field}:${value}`.toLowerCase();
  const tokens = q.split(/\s+/).filter(Boolean);
  const isField = (t: string) => t.toLowerCase().startsWith(`${field}:`);
  const had = tokens.some(t => t.toLowerCase() === term);
  const rest = tokens.filter(t => !isField(t));
  return (had ? rest : [...rest, `${field}:${value}`]).join(' ');
}

function hasTerm(q: string, field: string, value: string): boolean {
  const term = `${field}:${value}`.toLowerCase();
  return q.split(/\s+/).some(t => t.toLowerCase() === term);
}

function FacetGroup({ title, buckets, render, field, query, onToggle }: {
  title: string;
  buckets: FacetBucket[];
  render: (b: FacetBucket) => React.ReactNode;
  field: string;
  query: string;
  onToggle: (field: string, value: string) => void;
}) {
  const [all, setAll] = useState(false);
  const rows = buckets.filter(b => b.count > 0);
  if (rows.length === 0) return null;
  const shown = all ? rows : rows.slice(0, FACET_PREVIEW);
  return (
    <section className="search-facet">
      <h3 className="search-facet-title">{title}</h3>
      <ul>
        {shown.map(b => {
          const value = field === 'verdict' && b.key === 'real_ioc' ? 'real' : b.key;
          const on = hasTerm(query, field, value);
          return (
            <li key={b.key}>
              <button
                type="button"
                className={`search-facet-row${on ? ' search-facet-row--on' : ''}`}
                aria-pressed={on}
                onClick={() => onToggle(field, value)}
              >
                <span className="search-facet-label">{render(b)}</span>
                <span className="search-facet-n">{on ? '×' : b.count.toLocaleString()}</span>
              </button>
            </li>
          );
        })}
      </ul>
      {rows.length > FACET_PREVIEW && (
        <button type="button" className="search-facet-more" onClick={() => setAll(a => !a)}>
          {all ? 'Show fewer' : `Show all ${rows.length}`}
        </button>
      )}
    </section>
  );
}

export default function SearchPage() {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const urlQuery = params.get('q') || '';
  const sort = params.get('sort') || 'relevance';
  const page = Math.max(1, parseInt(params.get('page') || '1', 10) || 1);

  const [input, setInput] = useState(urlQuery);
  const [query, setQuery] = useState(urlQuery.trim());

  // Follow ?q= only when it changes from outside (back/forward, a link);
  // our own commits must not rewrite the box mid-typing ("emotet ").
  const committed = useRef(urlQuery.trim());
  useEffect(() => {
    if (urlQuery.trim() === committed.current) return;
    committed.current = urlQuery.trim();
    setInput(urlQuery);
    setQuery(urlQuery.trim());
  }, [urlQuery]);

  const commit = useCallback((value: string) => {
    const q = value.trim();
    committed.current = q;
    setQuery(q);
    const next = new URLSearchParams();
    if (q) next.set('q', q);
    if (sort !== 'relevance') next.set('sort', sort);
    setParams(next, { replace: true });
  }, [sort, setParams]);

  // Search as you type.
  useEffect(() => {
    if (input.trim() === query) return;
    const t = setTimeout(() => commit(input), DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [input]); // eslint-disable-line react-hooks/exhaustive-deps

  const setQueryNow = useCallback((value: string) => {
    setInput(value);
    commit(value);
  }, [commit]);

  const setSort = (s: string) => {
    const next = new URLSearchParams(params);
    if (s !== 'relevance') next.set('sort', s); else next.delete('sort');
    next.delete('page');
    setParams(next, { replace: true });
  };

  const setPage = (p: number) => {
    const next = new URLSearchParams(params);
    if (p > 1) next.set('page', String(p)); else next.delete('page');
    setParams(next);
    window.scrollTo({ top: 0 });
  };

  const { data, isLoading, isFetching } = useQuery<ExploreSearchResponse>({
    queryKey: ['search', query, sort, page],
    queryFn: () => searchEntities({ query, sort_by: sort, limit: PAGE_SIZE, offset: (page - 1) * PAGE_SIZE }),
    enabled: !!query,
    placeholderData: keepPreviousData,
  });

  const { data: cats } = useQuery<ExploreCategoriesResponse>({
    queryKey: ['explore-categories'],
    queryFn: fetchExploreCategories,
    staleTime: 60_000,
  });
  const typeLabel = useMemo(() => {
    const m: Record<string, string> = { ...INTEL_LABELS };
    for (const g of cats?.groups ?? []) for (const c of g.categories) m[c.key] = c.label;
    return m;
  }, [cats]);

  const onToggle = useCallback((field: string, value: string) => {
    setQueryNow(toggleFacet(input, field, value));
  }, [input, setQueryNow]);

  const removeClause = useCallback((c: QueryClause) => {
    setQueryNow(cutSpan(input, c.start, c.end));
  }, [input, setQueryNow]);

  const pick = useCallback((e: ExploreSearchResult) => navigate(entityDetailPath(e)), [navigate]);

  const facets = query ? data?.facets : null;
  const clauses = query ? data?.clauses ?? [] : [];
  const notices = query ? data?.notices ?? [] : [];
  const hasFacets = !!facets && (
    (facets.category?.length ?? 0) > 0 || (facets.country?.length ?? 0) > 0 || (facets.verdict?.length ?? 0) > 0
  );

  // "/" focuses the box from anywhere on the page (global "/" lands here).
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const t = e.target as HTMLElement | null;
      if (e.key !== '/' || e.metaKey || e.ctrlKey || e.altKey) return;
      if (t && (t.isContentEditable || /^(INPUT|TEXTAREA|SELECT)$/.test(t.tagName))) return;
      e.preventDefault();
      document.querySelector<HTMLInputElement>('.search-page .qi-input')?.focus();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, []);

  const facetSidebar = hasFacets && facets ? (
    <>
      <FacetGroup
        title="Type" field="type" query={input} onToggle={onToggle}
        buckets={facets.category ?? []}
        render={(b) => (
          <>
            <span className="search-facet-dot" style={{ background: categoryColor(b.key) }} />
            {typeLabel[b.key] ?? b.key}
          </>
        )}
      />
      <FacetGroup
        title="Country" field="country" query={input} onToggle={onToggle}
        buckets={facets.country ?? []}
        render={(b) => (
          <span title={countryLabel(b.key) ?? b.key}>
            {countryFlag(b.key) && <span aria-hidden="true" className="search-facet-flag">{countryFlag(b.key)}</span>}
            {countryLabel(b.key) ?? b.key}
          </span>
        )}
      />
      <FacetGroup
        title="Verdict" field="verdict" query={input} onToggle={onToggle}
        buckets={facets.verdict ?? []}
        render={(b) => (b.key === 'real_ioc' ? 'real IOC' : b.key.replace(/_/g, ' '))}
      />
    </>
  ) : null;

  return (
    <div className="search-page">
      <header className="search-head">
        <div className="search-box-row">
          <QueryInput
            mode="search"
            size="lg"
            value={input}
            onChange={setInput}
            onSubmit={setQueryNow}
            onPickEntity={pick}
            autoFocus
            ariaLabel="Search entities"
            placeholder="Search actors, families, IOCs, CVEs, techniques… try actor:apt country:RU"
          />
          {query && (
            <select className="explore-sort-select" value={sort} onChange={(e) => setSort(e.target.value)} aria-label="Sort results">
              <option value="relevance">Best match</option>
              <option value="doc_count">Most referenced</option>
              <option value="value">Alphabetical</option>
            </select>
          )}
        </div>

        {notices.length > 0 && (
          <div className="explore-notices" role="status">
            {notices.map(n => <div key={n} className="explore-notice">{n}</div>)}
          </div>
        )}

        {clauses.some(c => c.kind !== 'text') && (
          <div className="explore-clauses" aria-label="Active search terms">
            <span className="explore-clauses-label">Searching</span>
            {clauses.map(c => (
              <span key={`${c.start}-${c.label}`} className={`explore-clause explore-clause--${c.kind}`}>
                {c.label}
                <button type="button" className="explore-clause-x" onClick={() => removeClause(c)}
                        aria-label={`Remove ${c.label}`} title="Remove">&times;</button>
              </span>
            ))}
          </div>
        )}
      </header>

      {!query ? (
        <section className="search-intro">
          <h1 className="search-intro-title">Search</h1>
          <p className="search-intro-lead">
            Find any actor, malware family, indicator, CVE or ATT&amp;CK technique. Type a name, or
            narrow down with filters. Every filter is optional.
          </p>
          <ul className="search-intro-examples">
            {SEARCH_EXAMPLES.map(ex => (
              <li key={ex.query}>
                <button type="button" onClick={() => setQueryNow(ex.query)}>
                  <code className="qi-code">{ex.query}</code>
                </button>
                <span>{ex.detail}</span>
              </li>
            ))}
          </ul>
          <p className="search-intro-foot">
            Press <kbd>/</kbd> to jump to the search box, <kbd>Tab</kbd> to complete.
            Rather browse? <Link to="/explore">Explore by category</Link>.
          </p>
        </section>
      ) : (
        <div className={`search-body${hasFacets ? '' : ' search-body--no-facets'}`}>
          {facetSidebar && (
            <>
              <aside className="search-facets search-facets--side" aria-label="Filters">{facetSidebar}</aside>
              <details className="search-facets search-facets--drawer">
                <summary>Filters</summary>
                {facetSidebar}
              </details>
            </>
          )}
          <div className={`search-results${isFetching && !isLoading ? ' search-results--stale' : ''}`}>
            <SearchResults
              data={data}
              isLoading={isLoading}
              page={page}
              pageSize={PAGE_SIZE}
              onPageChange={setPage}
              onTryExample={setQueryNow}
            />
          </div>
        </div>
      )}
    </div>
  );
}
